'use client';

import { useTranslations } from '@/hooks/useTranslations';
import {
  Sparkles,
  Code2,
  Globe2,
  Heart,
  Leaf,
  type LucideIcon,
} from 'lucide-react';
import { FaLinkedin } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { withLocaleHref, type Locale } from '@/i18n/config';
import { useLocaleWithFallback } from '@/hooks/useLocale';

const FOUNDER_LINKEDIN_URL = 'https://www.linkedin.com/in/jovansmiljanic';

type Principle = {
  icon: LucideIcon;
  titleKey: string;
  descKey: string;
  featured?: boolean;
};

const PRINCIPLES: Principle[] = [
  { icon: Sparkles, titleKey: 'value1Title', descKey: 'value1Desc' },
  { icon: Code2, titleKey: 'value2Title', descKey: 'value2Desc' },
  { icon: Globe2, titleKey: 'value3Title', descKey: 'value3Desc' },
  {
    icon: Heart,
    titleKey: 'value4Title',
    descKey: 'value4Desc',
    featured: true,
  },
];

export function AboutPage() {
  const t = useTranslations('About');
  const locale = useLocaleWithFallback();

  return (
    <div className="bg-background text-foreground">
      <main className="max-w-5xl mx-auto w-full px-6 md:px-12 pt-16 md:pt-24 pb-24 space-y-32">
        <HeroSection t={t} />
        <PrinciplesSection t={t} />
        <StorySection t={t} />
        <CtaSection t={t} locale={locale} />
      </main>
    </div>
  );
}

type T = (key: string) => string;

function HeroSection({ t }: { t: T }) {
  return (
    <section className="text-center space-y-8 pt-6">
      <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full bg-surface-high ghost-border text-brand font-medium text-sm primary-glow">
        <Leaf className="w-4 h-4" />
        {t('heroBadge')}
      </div>
      <h1 className="font-bold text-5xl md:text-7xl tracking-[-0.03em] leading-tight">
        {t('heroHeadline')}
        <br />
        <span className="text-transparent bg-clip-text bg-linear-to-r from-brand to-brand-hover">
          {t('heroAccent')}
        </span>
      </h1>
      <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
        {t('heroSubtitle')}
      </p>
    </section>
  );
}

function PrinciplesSection({ t }: { t: T }) {
  return (
    <section className="space-y-12">
      <div className="text-center">
        <h2 className="text-3xl font-bold tracking-tight mb-4">
          {t('principlesTitle')}
        </h2>
        <p className="text-muted-foreground max-w-lg mx-auto">
          {t('principlesSubtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {PRINCIPLES.map((p) => (
          <PrincipleCard key={p.titleKey} principle={p} t={t} />
        ))}
      </div>
    </section>
  );
}

function PrincipleCard({ principle, t }: { principle: Principle; t: T }) {
  const { icon: Icon, titleKey, descKey, featured } = principle;
  return (
    <div
      className={`relative overflow-hidden rounded-xl p-8 ghost-border transition-transform hover:-translate-y-1 duration-300 ${
        featured
          ? 'bg-linear-to-b from-surface-high to-card primary-glow'
          : 'bg-surface-high'
      }`}
    >
      {featured && (
        <div className="absolute top-0 right-0 w-40 h-40 bg-brand/10 rounded-full blur-3xl pointer-events-none" />
      )}
      <div className="relative z-10">
        <div className="w-12 h-12 bg-surface-bright rounded-xl flex items-center justify-center mb-6 ghost-border">
          <Icon className="w-5 h-5 text-brand" />
        </div>
        <h3 className="text-xl font-bold mb-3">{t(titleKey)}</h3>
        <p className="text-muted-foreground leading-relaxed">{t(descKey)}</p>
      </div>
    </div>
  );
}

function StorySection({ t }: { t: T }) {
  return (
    <section className="max-w-3xl mx-auto space-y-8">
      <h2 className="text-3xl font-bold tracking-tight">{t('storyTitle')}</h2>
      <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
        <p>{t('visionP1')}</p>
        <p>{t('visionP2')}</p>
        <p>{t('missionP1')}</p>
        <p>{t('visionP3')}</p>
      </div>

      <div className="space-y-3 pt-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">
          {t('teamTitle')}
        </p>
        <div className="flex items-center gap-2">
          <span className="font-bold text-foreground">
            {t('teamMemberName')}
          </span>
          <a
            href={FOUNDER_LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
            className="text-brand hover:opacity-80 transition-opacity"
          >
            <FaLinkedin className="w-4 h-4" />
          </a>
        </div>
        <p className="text-muted-foreground leading-relaxed">
          {t('teamMemberBio')}
        </p>
      </div>
    </section>
  );
}

function CtaSection({ t, locale }: { t: T; locale: Locale }) {
  return (
    <section className="relative overflow-hidden rounded-xl bg-linear-to-br from-surface-high to-surface-mid p-12 md:p-16 text-center ghost-border">
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-brand/12 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-brand/7 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 max-w-2xl mx-auto space-y-6">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
          {t('ctaTitle')}
        </h2>
        <p className="text-lg text-muted-foreground">{t('ctaSubtitle')}</p>
        <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            asChild
            variant="brand"
            size="lg"
            className="px-8 py-6 text-base font-bold rounded-xl primary-glow"
          >
            <Link href={withLocaleHref('/register', locale)}>
              {t('ctaPrimary')}
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            className="px-8 py-6 text-base font-medium rounded-xl bg-surface-bright text-foreground hover:bg-surface-high ghost-border"
          >
            <Link href={withLocaleHref('/', locale)}>{t('ctaSecondary')}</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
